import { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";

const einvoicingAdminBase = `${baseURL}/einvoicing/admin/landlords`;

const einvoicingErrorMessage = (error, fallback) => {
	const data = error?.response?.data;
	if (!data) return error?.message || fallback;
	if (typeof data === "string") return data;
	if (data.message) return data.message;
	if (data.error) {
		if (typeof data.error === "string") return data.error;
		if (Array.isArray(data.error)) return data.error[0];
	}
	return fallback;
};

/** Enable / disable landlord e-invoicing (URA EFRIS provider). */
export const useEinvoicing = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

	const [isSubmitting, setSubmitting] = useState(false);

	const setEinvoicingEnabled = async (landlordId, einvoicing_enabled) => {
		if (!landlordId) return { success: false, message: "Missing landlord." };
		setSubmitting(true);
		try {
			const response = await axios.post(`${einvoicingAdminBase}/${landlordId}/enable`, {
				einvoicing_enabled,
			});
			return { success: true, data: response.data };
		} catch (error) {
			return {
				success: false,
				message: einvoicingErrorMessage(error, "Failed to update e-invoicing."),
			};
		} finally {
			setSubmitting(false);
		}
	};

	const issueInvoice = async (landlordId, payload) => {
		if (!landlordId) {
			const err = new Error("Missing landlord.");
			err.response = { data: { error: "Missing landlord." } };
			throw err;
		}
		const response = await axios.post(
			`${einvoicingAdminBase}/${landlordId}/invoices`,
			payload
		);
		return response.data;
	};

	const issueEfrisForPayment = async (landlordId, paymentId) => {
		if (!landlordId || !paymentId) {
			return { success: false, message: "Missing landlord or payment." };
		}
		setSubmitting(true);
		try {
			const data = await issueInvoice(landlordId, { payment_id: paymentId });
			return { success: true, data };
		} catch (error) {
			return {
				success: false,
				message: einvoicingErrorMessage(error, "Failed to generate EFRIS receipt."),
			};
		} finally {
			setSubmitting(false);
		}
	};

	const run = async (request, fallback) => {
		setSubmitting(true);
		try {
			const response = await request();
			return { success: true, data: response.data };
		} catch (error) {
			return {
				success: false,
				message: einvoicingErrorMessage(error, fallback),
				data: error?.response?.data,
			};
		} finally {
			setSubmitting(false);
		}
	};

	const onboardingUrl = (landlordId, path = "") =>
		`${einvoicingAdminBase}/${landlordId}/onboarding${path}`;

	const getOnboarding = (landlordId) =>
		run(() => axios.get(onboardingUrl(landlordId)), "Failed to load EFRIS onboarding.");

	const generateKeys = (landlordId, force = false) =>
		run(
			() => axios.post(onboardingUrl(landlordId, "/generate-keys"), { force }),
			"Failed to generate keys."
		);

	const saveDevice = (landlordId, payload) =>
		run(
			() => axios.put(onboardingUrl(landlordId, "/device"), payload),
			"Failed to save device details."
		);

	const verifyConnection = (landlordId) =>
		run(
			() => axios.post(onboardingUrl(landlordId, "/verify"), {}),
			"EFRIS verification failed."
		);

	const registerProducts = (landlordId) =>
		run(
			() => axios.post(onboardingUrl(landlordId, "/register-products"), {}),
			"Failed to register products."
		);

	const saveSettings = (landlordId, payload) =>
		run(
			() => axios.put(onboardingUrl(landlordId, "/settings"), payload),
			"Failed to save EFRIS settings."
		);

	const downloadFile = async (url, filename, fallback) => {
		const result = await run(() => axios.get(url, { responseType: "blob" }), fallback);
		if (!result.success) {
			// Blob error bodies hide the JSON message.
			const blob = result.data;
			if (blob instanceof Blob) {
				try {
					const parsed = JSON.parse(await blob.text());
					return { success: false, message: parsed.error || fallback };
				} catch (e) {
					return { success: false, message: fallback };
				}
			}
			return result;
		}
		const href = window.URL.createObjectURL(result.data);
		const link = document.createElement("a");
		link.href = href;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(href);
		return { success: true };
	};

	const downloadPublicKey = (landlordId, type = "crt") =>
		downloadFile(
			onboardingUrl(landlordId, `/public-key?type=${type}`),
			`efris_public_key_${landlordId}.${type}`,
			"Failed to download public key."
		);

	const downloadInstructions = (landlordId) =>
		downloadFile(
			onboardingUrl(landlordId, "/instructions"),
			`efris_setup_instructions_${landlordId}.pdf`,
			"Failed to download instructions."
		);

	const downloadReceiptPdf = (landlordId, invoiceId, fdn) =>
		downloadFile(
			`${einvoicingAdminBase}/${landlordId}/invoices/${invoiceId}/pdf`,
			`efris_receipt_${fdn || invoiceId}.pdf`,
			"Failed to download EFRIS receipt."
		);

	return {
		isSubmitting,
		setEinvoicingEnabled,
		issueEfrisForPayment,
		issueInvoice,
		getOnboarding,
		generateKeys,
		saveDevice,
		verifyConnection,
		registerProducts,
		saveSettings,
		downloadPublicKey,
		downloadInstructions,
		downloadReceiptPdf,
	};
};
